import { useMemo, useState } from 'react';
import { Layout } from '../../components/Layout';
import Content from './Content';
import { useSelector } from 'react-redux';
import {
  selectActiveSubscription,
  selectSubscriptionSummaries,
  setActiveSubscriptionId,
} from '../../store/slices/subscription';
import { useDispatch } from 'react-redux';
import { SubscriptionSummary } from '../../types/subscription';
import PeriodProfitChart from '../Detail/Charts/PeriodProfitChart';

export enum SubscriptionTabs {
  Overview = 'OVERVIEW',
  Transactions = 'TRANSACTIONS',
  Calculations = 'CALCULATIONS',
  Manage = 'MANAGE',
}

const Subscription = () => {
  const dispatch = useDispatch();

  const subscriptionSummaries = useSelector(selectSubscriptionSummaries);
  const activeSubscription = useSelector(selectActiveSubscription);

  const [activeTab, setActiveTab] = useState(SubscriptionTabs.Overview);

  const susbcriptionsByCluster = useMemo(() => {
    return subscriptionSummaries.reduce<
      Record<string, Array<SubscriptionSummary>>
    >(
      (acc, item) => (
        (acc[item.clusterId] = acc[item.clusterId] || []).push(item), acc
      ),
      {},
    );
  }, [subscriptionSummaries]);

  const handleSubscriptionClick = (subscriptionId: number) => {
    dispatch(setActiveSubscriptionId(subscriptionId));
  };

  const hasPositiveProfit = !activeSubscription
    ? undefined
    : (activeSubscription.currentProfit || 0) >= 0;

  const profitSection = useMemo(() => {
    const profits = activeSubscription?.profits;

    const items = [
      {
        label: 'Total Stable Profit',
        delta: profits?.total.stable.relative || 0,
      },
      {
        label: 'Total Asset Profit',
        delta: profits?.total.asset.relative || 0,
      },
      {
        label: 'Market Difference',
        delta: profits?.noAlgorithmicProgressionProfit.relative || 0,
      },
    ];

    if (activeSubscription?.latestTransaction) {
      items.splice(2, 0, {
        label:
          activeSubscription.latestTransaction?.orderType === 'BUY'
            ? 'Current Stable Profit'
            : 'Current Asset Profit',
        delta: activeSubscription.currentProfit || 0,
      });
    }

    return items;
  }, [
    activeSubscription?.currentProfit,
    activeSubscription?.latestTransaction,
    activeSubscription?.profits,
  ]);

  const sections = useMemo(() => {
    return [
      {
        label: 'Overview',
        isActive: activeTab === SubscriptionTabs.Overview,
        onClick: () => setActiveTab(SubscriptionTabs.Overview),
      },
      {
        label: 'Transactions',
        isActive: activeTab === SubscriptionTabs.Transactions,
        onClick: () => setActiveTab(SubscriptionTabs.Transactions),
      },
      {
        label: 'Calculations',
        isActive: activeTab === SubscriptionTabs.Calculations,
        onClick: () => setActiveTab(SubscriptionTabs.Calculations),
      },
      {
        label: 'Manage',
        isActive: activeTab === SubscriptionTabs.Manage,
        onClick: () => setActiveTab(SubscriptionTabs.Manage),
      },
    ];
  }, [activeTab]);

  return (
    <Layout hasPositiveProfit={hasPositiveProfit}>
      {!!activeSubscription ? (
        <Layout.SideMenu hasPositiveProfit={hasPositiveProfit}>
          <Layout.SideMenuSection>
            <Layout.SideMenuTitle title="Sections" />
            <>
              {sections.map((section) => (
                <Layout.SideMenuItem
                  key={section.label}
                  id={section.label}
                  label={section.label}
                  isActive={section.isActive}
                  onClick={section.onClick}
                />
              ))}
            </>
          </Layout.SideMenuSection>

          {profitSection ? (
            <Layout.SideMenuSection>
              <Layout.SideMenuTitle title="Profit" />
              <>
                {profitSection.map((profitItem) => (
                  <Layout.SideMenuItem
                    key={profitItem.label}
                    id={profitItem.label}
                    label={profitItem.label}
                    delta={profitItem.delta}
                  />
                ))}
              </>
              {activeSubscription?.chartData?.dataPoints.total ? (
                <PeriodProfitChart
                  data={activeSubscription?.chartData?.dataPoints.total}
                />
              ) : null}
            </Layout.SideMenuSection>
          ) : undefined}

          <Layout.SideMenuSection>
            <Layout.SideMenuTitle title="Subscriptions" />
            <>
              {Object.entries(susbcriptionsByCluster).map(
                ([clusterId, subscriptions]) => (
                  <div key={clusterId}>
                    <Layout.SideMenuSubTitle title={clusterId} />
                    {subscriptions
                      .sort(
                        (a, b) =>
                          b.totalBalanceInStable - a.totalBalanceInStable,
                      )
                      .map((sub) => (
                        <Layout.SideMenuItem
                          key={sub.id}
                          id={sub.id}
                          label={sub.botFriendlyName}
                          value={sub.totalBalanceInStable}
                          delta={sub.currentProfit}
                          isActive={activeSubscription?.id === sub.id}
                          onClick={handleSubscriptionClick}
                        />
                      ))}
                  </div>
                ),
              )}
            </>
          </Layout.SideMenuSection>
        </Layout.SideMenu>
      ) : undefined}

      <Layout.Content>
        <Content activeTab={activeTab} subscription={activeSubscription} />
      </Layout.Content>
    </Layout>
  );
};

export default Subscription;
